<template>
  <div>
    <div class="page-head">
      <div>
        <h1 class="page-head__title">{{ t("security.title") }}</h1>
        <p class="page-head__lede">{{ t("security.lede") }}</p>
      </div>
      <span :class="['badge', session.totpEnabled ? 'badge--success' : 'badge--warn']">
        {{ session.totpEnabled ? t("security.enabled") : t("security.disabled") }}
      </span>
    </div>

    <div v-if="error" class="notice notice--danger" style="margin-bottom: var(--space-4)">
      <div class="notice__body">{{ error }}</div>
    </div>

    <div class="card">
      <h2 class="card__title">{{ t("security.totpTitle") }}</h2>
      <p class="card__sub">{{ t("security.totpLede") }}</p>

      <div v-if="step === 'done' || (session.totpEnabled && step === 'idle')" class="notice notice--success">
        <div class="notice__body">{{ t("security.enabledBody") }}</div>
      </div>

      <form v-else-if="step === 'idle'" class="security__form" @submit.prevent="enable">
        <div class="field">
          <label class="field__label" for="pw">{{ t("security.confirmPassword") }}</label>
          <input id="pw" v-model="password" class="input" type="password" autocomplete="current-password" required />
          <span class="field__hint">{{ t("security.confirmPasswordHint") }}</span>
        </div>
        <button class="btn" type="submit" :disabled="busy || !password">
          <span v-if="busy" class="spinner" aria-hidden="true"></span>
          {{ t("security.start") }}
        </button>
      </form>

      <template v-else-if="step === 'scan'">
        <div class="security__enrol">
          <div>
            <QrCode :value="enrolment.totpURI" :label="t('security.qrLabel')" />
          </div>
          <div class="stack">
            <p class="small">{{ t("security.scan") }}</p>
            <dl class="kv">
              <dt>{{ t("security.secret") }}</dt>
              <dd><code class="break-all">{{ groupedSecret }}</code></dd>
              <dt>{{ t("security.uri") }}</dt>
              <dd><code class="break-all xs">{{ enrolment.totpURI }}</code></dd>
            </dl>
            <button type="button" class="btn btn--secondary btn--sm" @click="copy(enrolment.totpURI, 'uri')">
              {{ copied === "uri" ? t("common.copied") : t("security.copyUri") }}
            </button>
          </div>
        </div>

        <div v-if="enrolment.backupCodes.length" class="notice notice--warn security__codes">
          <div class="notice__body">
            <p class="strong">{{ t("security.backupTitle") }}</p>
            <p class="small">{{ t("security.backupOnce") }}</p>
            <ul class="security__list mono">
              <li v-for="c in enrolment.backupCodes" :key="c">{{ c }}</li>
            </ul>
            <div class="row">
              <button type="button" class="btn btn--secondary btn--sm" @click="copy(enrolment.backupCodes.join('\n'), 'codes')">
                {{ copied === "codes" ? t("common.copied") : t("security.copyCodes") }}
              </button>
              <button type="button" class="btn btn--secondary btn--sm" @click="downloadCodes">{{ t("security.downloadCodes") }}</button>
            </div>
          </div>
        </div>

        <form class="security__form" @submit.prevent="confirm">
          <div class="field">
            <label class="field__label" for="code">{{ t("security.enterCode") }}</label>
            <input id="code" v-model.trim="code" class="input input--mono" inputmode="numeric"
                   autocomplete="one-time-code" maxlength="6" required />
          </div>
          <div class="row">
            <button class="btn" type="submit" :disabled="busy || code.length < 6">
              <span v-if="busy" class="spinner" aria-hidden="true"></span>
              {{ t("security.verify") }}
            </button>
            <button type="button" class="btn btn--ghost" :disabled="busy" @click="reset">{{ t("common.cancel") }}</button>
          </div>
        </form>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import { authApi } from "../lib/api";
import { session, refreshMe } from "../lib/session";
import { useErrorText } from "../lib/errors";
import QrCode from "../components/QrCode.vue";

const { t } = useI18n();
const errorText = useErrorText();

const step = ref("idle");
const password = ref("");
const code = ref("");
const busy = ref(false);
const error = ref("");
const copied = ref("");
const enrolment = reactive({ totpURI: "", backupCodes: [] });

const groupedSecret = computed(() => {
  try {
    const secret = new URL(enrolment.totpURI).searchParams.get("secret") || "";
    return secret.replace(/(.{4})/g, "$1 ").trim();
  } catch (e) {
    return "";
  }
});

async function run(fn) {
  busy.value = true;
  error.value = "";
  try {
    await fn();
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}

const enable = () => run(async () => {
  const result = await authApi.enableTwoFactor(password.value);
  password.value = "";
  enrolment.totpURI = result?.totpURI || "";
  enrolment.backupCodes = Array.isArray(result?.backupCodes) ? result.backupCodes : [];
  if (!enrolment.totpURI) throw new Error(t("security.noUri"));
  step.value = "scan";
});

const confirm = () => run(async () => {
  await authApi.verifyTotp(code.value);
  code.value = "";
  enrolment.totpURI = "";
  enrolment.backupCodes = [];
  await refreshMe();
  step.value = "done";
});

function reset() {
  step.value = "idle";
  code.value = "";
  enrolment.totpURI = "";
  enrolment.backupCodes = [];
}

async function copy(text, which) {
  try {
    await navigator.clipboard.writeText(text);
    copied.value = which;
    setTimeout(() => (copied.value = ""), 2000);
  } catch (e) {
    error.value = t("errors.clipboard");
  }
}

function downloadCodes() {
  const blob = new Blob([`${enrolment.backupCodes.join("\n")}\n`], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "zarokaa-developer-backup-codes.txt";
  a.click();
  URL.revokeObjectURL(url);
}
</script>

<style scoped>
.security__form { max-width: 24rem; margin-top: var(--space-4); }
.security__enrol { display: grid; grid-template-columns: 220px 1fr; gap: var(--space-5); align-items: start; margin-top: var(--space-4); }
.security__codes { margin-top: var(--space-5); }
.security__list { display: grid; grid-template-columns: repeat(auto-fill, minmax(9rem, 1fr)); gap: var(--space-1) var(--space-4); list-style: none; padding: 0; margin: var(--space-3) 0; }
@media (max-width: 640px) {
  .security__enrol { grid-template-columns: 1fr; }
}
</style>
